import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, effect, inject } from '@angular/core';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { UiComponent } from '@df/ui/ui.component';
import { BehaviorSubject } from 'rxjs';
import { Product } from '../catalog/product/product.decorator';
import { DeliveryPostcodeService } from '../common/delivery/delivery-postcode.service';
import { NCmsDelivery } from '../common/delivery/modal/delivery-modal.component';
import { LoaderComponent } from '../common/loader/loader.component';
import { SharedModule } from '../shared.module';

type TState = 'nopostcode' | 'none' | 'busy' | 'instock' | 'outofstock' | 'error';

@Component({
    selector: 'product-delivery-state',
    templateUrl: 'product-delivery-state.html',
    styleUrls: ['product-delivery-state.scss'],
    standalone: true,
    imports: [LoaderComponent, SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDeliveryStateComponent extends UiComponent {
    /**
     * Product
     */
    @Input({
        required: true
    })
    product!: Product<IProductDataV2>;

    @Input()
    qty = 1;

    @Input()
    reducedSize = false;

    /**
     * Component display variant (depends on context)
     */
    @Input({ required: true })
    variant!: 'PLP' | 'PDP' | 'basket' | 'googleShopping';

    @Input()
    googleShoppingLayout = false;

    /**
     * Component state
     */
    protected state: TState = 'busy';

    /**
     * Postcode selected for delivery
     */
    protected postcode: string | undefined;

    /**
     * Error messgae
     */
    protected error: string | undefined;

    /**
     * Add to collection status
     */
    protected buttonStatus = new Status();

    /**
     * Submission with given branch number
     */
    @Output()
    onsubmit = new EventEmitter<{ status: Status }>();

    /**
     * If method is availble
     */
    @Output()
    available = new BehaviorSubject<boolean | 'noselection'>(false);

    /**
     * Services
     */
    private dfModalService = inject(DfModalService);
    private deliveryPostcode = inject(DeliveryPostcodeService);

    constructor() {
        super();
        effect(() => {
            const postcode = this.deliveryPostcode.postcode();
            if (postcode !== this.postcode) {
                this.postcode = postcode;
                this.updateData();
            }
        });
    }

    /**
     * On changes hook to update data on inputs changes
     */
    override ngOnChanges(changes: any): void {
        super.ngOnChanges(changes);
        this.updateData();
    }

    /**
     * Open branch selection modal
     */
    protected async openPostocodeSelection(submitOnBranchSelection?: boolean) {
        const data: NCmsDelivery.IModalData = {
            productId: this.product!.id
        };
        this.dfModalService.open(this.buttonStatus, NCmsDelivery.MODAL_ID, undefined, data, modalRef => {
            // if we aim to submit after branch is set we need to add callback on modal closed
            if (submitOnBranchSelection) {
                modalRef?.afterClosed().subscribe(async () => {
                    await this.updateData();
                    if (this.state === 'instock') {
                        this.submit();
                    }
                });
            }
        });
    }

    /**
     * If postcode is not selected open modal,
     * if can add then submit
     */
    protected submit() {
        switch (this.state) {
            case 'nopostcode':
                return this.openPostocodeSelection(true);
            case 'instock':
                return this.onsubmit.emit({ status: this.buttonStatus });
        }
    }

    /**
     * Update component state on changes
     */
    private async updateData() {
        if (!this.product.data?.is_delivery) {
            this.setAvailable(false);
            return this.setState('none');
        } else if (!this.postcode) {
            this.setAvailable('noselection');
            return this.setState('nopostcode');
        }
        this.setState('busy');
        if (this.deliveryPostcode.postcode()) {
            const sku = this.product.data!.sku!;
            try {
                const stockQty = sku ? await this.deliveryPostcode.retrieve(this.product) : 0;
                if (stockQty) {
                    this.setState('instock');
                } else {
                    this.setState('outofstock');
                }
            } catch (err) {
                this.error = err instanceof Error ? err.message : err!.toString();
                return this.setState('error');
            }
        }

        this.setAvailable(this.state === 'instock');
    }

    private setAvailable(available: boolean | 'noselection') {
        if (this.available.value !== available) {
            this.available.next(available);
        }
    }

    /**
     * Component state setter
     */
    private setState(state: TState) {
        if (this.state !== state) {
            this.state = state;
            this.detectChanges();
        }
    }

    get stateColor() {
        switch (this.state) {
            case 'error':
                return 'col-2';
            case 'outofstock':
                return 'col-4';
            case 'none':
                return 'col-4';
            case 'instock':
                return this.variant === 'basket' ? 'col-3' : 'col-1';
            default:
                return 'col-1';
        }
    }
}
